import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useParticipants } from "../../contexts/ParticipantContext";
import BackButton from "../Common/BackButton";

const STATUS_LABEL = {
  created: "Created",
  drying: "Drying",
  bisque: "Bisque Fired",
  glazed: "Glazed",
  glaze_fired: "Glaze Fired",
  ready: "Ready for Pickup",
  collected: "Collected",
  received: "Received",
  "bisque fired": "Bisque Fired",
  "glaze fired": "Glaze Fired",
  "ready for pickup": "Ready for Pickup",
};

const LS_MYPIECES_EMAIL = "mudo:myPieces:lastEmail";

function labelFor(piece) {
  const raw = (piece.status || piece.stage || "").toString().trim();
  if (!raw) return "—";
  const norm = raw.toLowerCase();
  return STATUS_LABEL[norm] || raw;
}

export default function MyPieces() {
  const { findPiecesByEmail } = useParticipants() || {};
  const [searchParams] = useSearchParams();

  const initialEmail =
    (searchParams.get("email") || localStorage.getItem(LS_MYPIECES_EMAIL) || "").trim();

  const [email, setEmail] = useState(initialEmail);
  const [results, setResults] = useState([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!initialEmail) return;
    doSearch(initialEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validEmail = useMemo(() => {
    if (!email.trim()) return false;
    // very light validation; we just want obvious mistakes filtered out
    return /.+@.+\..+/.test(email.trim());
  }, [email]);

  function doSearch(em) {
    const needle = (em || email || "").trim().toLowerCase();
    if (!needle || typeof findPiecesByEmail !== "function") {
      setResults([]);
      return;
    }
    try {
      const hits = findPiecesByEmail(needle) || [];
      setResults(hits);
      localStorage.setItem(LS_MYPIECES_EMAIL, (em || email).trim());
    } catch {
      setResults([]);
    }
  }

  const onSubmit = (e) => {
    e?.preventDefault?.();
    if (!validEmail) return;
    doSearch();
    setTouched(false);
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-[100] bg-[#1b1b1c] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/src/assets/mudo-logo-bg.png"
              alt="Mudo"
              className="w-5 h-5 opacity-90"
            />
            <h1 className="text-lg font-bold">Track Your Pieces</h1>
          </div>
          <BackButton to="/dashboard" label="Back to Dashboard" />
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-6 max-w-3xl mx-auto">
        <form onSubmit={onSubmit} className="flex gap-2 mb-4">
          <input
            className="border p-2 rounded flex-1 bg-white text-black"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setTouched(true);
            }}
          />
          <button
            className={`px-4 py-2 rounded ${
              validEmail ? "bg-black text-white" : "bg-black/40 text-white/60 cursor-not-allowed"
            }`}
            type="submit"
            disabled={!validEmail}
            title={validEmail ? "Search" : "Enter a valid email to search"}
          >
            Check
          </button>
          {touched && (
            <button
              type="button"
              onClick={() => {
                setEmail("");
                setResults([]);
                setTouched(false);
              }}
              className="px-3 py-2 rounded bg-zinc-700"
              title="Clear"
            >
              Clear
            </button>
          )}
        </form>

        {results.length > 0 && (
          <div className="text-xs opacity-75 mb-2">{results.length} piece{results.length === 1 ? "" : "s"} found</div>
        )}

        {results.length === 0 ? (
          <div className="text-sm opacity-70">
            {validEmail
              ? "No pieces found yet. If you just booked, your pieces may appear shortly."
              : "Enter your email to see your pieces’ status."}
          </div>
        ) : (
          <div className="grid gap-3">
            {results.map((p) => (
              <div key={p.id} className="border rounded p-3 bg-white text-black">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{p.title || p.defaultTitle || "Untitled"}</div>
                  <div className="text-xs px-2 py-1 rounded bg-black text-white">
                    {labelFor(p)}
                  </div>
                </div>
                <div className="text-xs opacity-70 mt-1">
                  Workshop: {p.workshopTitle || p.workshopId || "—"}
                </div>

                {/* timeline */}
                {Array.isArray(p.history) && p.history.length > 0 && (
                  <div className="mt-2 text-xs">
                    <div className="font-medium mb-1">History</div>
                    <ul className="space-y-1">
                      {p.history
                        .slice()
                        .reverse()
                        .map((h, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="opacity-60">
                              {h.at ? new Date(h.at).toLocaleString() : "—"}
                            </span>
                            <span>→</span>
                            <span className="font-medium">
                              {STATUS_LABEL[(h.to || "").toLowerCase()] || h.to || "—"}
                            </span>
                            {h.note && (
                              <span className="opacity-60">• {h.note}</span>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
